import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/lib/api';
import type { User, LoginCredentials, RegisterData } from '@/types';

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User | null) => void;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  verifyAuth: () => Promise<void>;

  // Permission checks
  canCreatePrompt: () => boolean;
  canEditPrompt: (promptCreatedBy?: string) => boolean;
  canDeletePrompt: () => boolean;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
        }),

      login: async (credentials) => {
        try {
          const response = await api.auth.login(credentials);
          set({
            user: response.data?.user || null,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ user: null, isAuthenticated: false, isLoading: false });
          throw error;
        }
      },

      register: async (data) => {
        try {
          const response = await api.auth.register(data);
          set({
            user: response.data?.user || null,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ user: null, isAuthenticated: false, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.auth.logout();
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      verifyAuth: async () => {
        try {
          const response = await api.auth.verify();
          set({
            user: response.data?.user || null,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      // 检查是否有创建 Prompt 权限
      canCreatePrompt: () => {
        const { user } = get();
        if (!user) return false;
        return user.role === 'admin' || user.permissionGroups?.includes('contributor');
      },

      // 检查是否可以编辑指定 Prompt
      canEditPrompt: (promptCreatedBy?: string) => {
        const { user } = get();
        if (!user) return false;

        // 管理员可以编辑所有
        if (user.role === 'admin') return true;

        // 贡献者只能编辑自己的
        if (user.permissionGroups?.includes('contributor')) {
          // 如果没有提供 promptCreatedBy，说明是新建，允许
          if (!promptCreatedBy) return true;
          // 检查是否是自己创建的
          return user.id === promptCreatedBy;
        }

        return false;
      },

      // 检查是否可以删除 Prompt（只有管理员）
      canDeletePrompt: () => {
        const { user } = get();
        if (!user) return false;
        return user.role === 'admin';
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user }),
    }
  )
);

